import React from 'react';
import styles from './Story.module.css';
import useScrollAnimation from '../hooks/useScrollAnimation';

const TIMELINE = [
  {
    year: '2003',
    heading: 'The Birth of a Legend',
    body: `Lineage II emerged from Korea, captivating millions with its stunning graphics, intense siege warfare, and deeply social gameplay. We were among those early pioneers, forming friendships that would last decades. The world of Aden was not just a game - it was a second home, a place where rivalries and alliances felt as real as anything outside the screen.`,
  },
  {
    year: '2010s',
    heading: 'The Dark Ages',
    body: `As time passed, the game we loved began to change. Pay-to-win mechanics crept in, communities fragmented, and the magic faded. We watched, we played, we hoped - but we knew we could do better. Something had to be done to preserve what made this world extraordinary.`,
  },
  {
    year: 'The Vision',
    heading: 'A Dream Takes Shape',
    body: `What if we could create a server that honored the original spirit of Lineage II? What if progression meant something again? These were not just questions - they became our mission. We gathered a small team of veterans, united by a single belief: that this community deserved something special.`,
  },
  {
    year: 'Today',
    heading: 'Lineage II Guardians Community',
    body: `Here we stand - a thriving realm built on passion, transparency, and respect. We are not just running a server; we are nurturing a community where every player matters and every achievement is earned. From first login to castle siege, your journey here is your own.`,
  },
  {
    year: 'Tomorrow',
    heading: 'The Journey Continues',
    body: `Our story is far from over - it is just beginning. With every new player who joins, every clan that rises, we write new chapters together. The best moments of Lineage II Community have not happened yet.`,
  },
];

const PILLARS = [
  {
    title: 'Pure Love for the Game',
    body: 'We are players first, developers second. Every decision comes from thousands of hours spent in Aden.',
  },
  {
    title: 'Community Above All',
    body: 'Your voice matters here. We listen, adapt, and grow together. Every player is part of the family.',
  },
  {
    title: 'Unwavering Fairness',
    body: 'Zero pay-to-win, period. Your success is determined by skill and dedication - never your wallet.',
  },
];

const PROMISES = [
  { num: '1', title: 'Preserve the Magic', body: 'We honor the classic Lineage II experience while introducing quality-of-life improvements.' },
  { num: '2', title: 'Build for Longevity', body: 'No shortcuts, no cash grabs. We are here for years, not months.' },
  { num: '3', title: 'Complete Transparency', body: 'Drop rates, mechanics, changes - everything is documented and shared.' },
  { num: '4', title: 'Foster Real Connections', body: 'MMOs are about people. We create spaces that bring players together.' },
];

function RevealSection({ children, className }) {
  const [ref, visible] = useScrollAnimation();
  return (
    <div ref={ref} className={`reveal ${visible ? 'visible' : ''} ${className || ''}`}>
      {children}
    </div>
  );
}

export default function Story() {
  return (
    <main className={styles.main}>
      <section className={`page-hero ${styles.hero}`} aria-label="Story hero">
        <h1>Our Story</h1>
        <p className={styles.heroSub}>Where Legends Are Born, and Communities Thrive</p>
      </section>

      {/* Intro */}
      <RevealSection>
        <section className={styles.intro}>
          <div className={styles.introInner}>
            <p className={styles.introText}>
              In a world where countless private servers rise and fall, we dared to dream of something different.
              This is not just a server - it is a promise. A promise to preserve the magic of Lineage II while
              building a home for adventurers who seek more than just another grind.
            </p>
          </div>
        </section>
      </RevealSection>

      {/* Timeline */}
      <RevealSection>
        <section className={styles.timelineSection} aria-label="Our history">
          <h2 className="section-title">Our Journey</h2>
          <hr className="gold-divider" />
          <div className={styles.timeline}>
            {TIMELINE.map(({ year, heading, body }, idx) => (
              <article key={heading} className={`${styles.timelineItem} ${idx % 2 === 0 ? styles.itemLeft : styles.itemRight}`}>
                <div className={styles.timelineYear}>{year}</div>
                <div className={styles.timelineContent}>
                  <h3 className={styles.timelineHeading}>{heading}</h3>
                  <p className={`${styles.timelineBody} ${idx === 0 ? styles.dropCap : ''}`}>{body}</p>
                </div>
              </article>
            ))}
          </div>
        </section>
      </RevealSection>

      <hr className="gold-divider-full" />

      {/* What Drives Us */}
      <RevealSection>
        <section className={styles.pillarsSection} aria-label="Our values">
          <h2 className="section-title">What Drives Us</h2>
          <hr className="gold-divider" />
          <div className={styles.pillarsGrid}>
            {PILLARS.map(({ title, body }) => (
              <div key={title} className={styles.pillarCard}>
                <div className={styles.pillarAccent} aria-hidden="true" />
                <h3 className={styles.pillarTitle}>{title}</h3>
                <p className={styles.pillarBody}>{body}</p>
              </div>
            ))}
          </div>
        </section>
      </RevealSection>

      <hr className="gold-divider-full" />

      {/* Our Promise */}
      <RevealSection>
        <section className={styles.promisesSection} aria-label="Our promise">
          <h2 className="section-title">Our Promise to You</h2>
          <hr className="gold-divider" />
          <div className={styles.promisesGrid}>
            {PROMISES.map(({ num, title, body }) => (
              <div key={num} className={styles.promiseCard}>
                <span className={styles.promiseNum}>{num}</span>
                <h3 className={styles.promiseTitle}>{title}</h3>
                <p className={styles.promiseBody}>{body}</p>
              </div>
            ))}
          </div>
        </section>
      </RevealSection>

      <hr className="gold-divider-full" />

      {/* Closing quote */}
      <section className={styles.closingSection}>
        <blockquote className={styles.closingQuote}>
          In Lineage II, we found more than a game. We found brothers and sisters in arms, rivals who pushed us
          to be better, and memories that last a lifetime.
        </blockquote>
        <p className={styles.closingAttrib}>- The Founding Team</p>
      </section>
    </main>
  );
}
